import { useCallback, useEffect, useState } from 'react';

import { useVisible } from '~/hooks/useVisible';
import { People } from '~/services/Http';
import { useGetCompanyPosts } from '~/services/Http/hooks';

import { getNextAnnouncement } from './services';

export interface UseAnnouncementsWidgetOutput {
  announcements: People.CompanyPostDto[];
  visibleAnnouncement?: People.CompanyPostDto;
  loading: boolean;
  failedToLoad: boolean;
  showAllAnnouncementsDialog: boolean;
  handleReadAllButtonClick: () => void;
  handleShowAllAnnouncementsDialogClose: () => void;
  handleContentOnMouseEnter: () => void;
  handleContentOnMouseLeave: () => void;
  handleContentOnReadMoreDialogOpen: () => void;
  handleContentOnReadMoreDialogClose: () => void;
}

export const companyPostGetReqParams = {
  statuses: [People.CompanyPostStatusEnum.PUBLISHED],
};

export const useAnnouncementsWidget = (): UseAnnouncementsWidgetOutput => {
  const [isMouseOver, setIsMouseOver] = useState(false);
  const [isReadMoreDialogOpen, setIsReadMoreDialogOpen] = useState(false);
  const [visibleAnnouncement, setVisibleAnnouncement] =
    useState<People.CompanyPostDto>();

  const {
    isVisible: showAllAnnouncementsDialog,
    show: handleReadAllButtonClick,
    hide: handleShowAllAnnouncementsDialogClose,
  } = useVisible();

  const {
    loading: announcementsLoading,
    failedToLoad: failedToLoadAnnouncements,
    data: announcements,
  } = useGetCompanyPosts(companyPostGetReqParams);

  useEffect(() => {
    if (!visibleAnnouncement && announcements?.length) {
      setVisibleAnnouncement(announcements[0]);
    }
  }, [announcements, visibleAnnouncement]);

  useEffect(() => {
    if (!isMouseOver && announcements?.length && !isReadMoreDialogOpen) {
      const timer = setInterval(() => {
        setVisibleAnnouncement((currentVisibleAnnouncement) =>
          getNextAnnouncement(currentVisibleAnnouncement, announcements),
        );
      }, 5000);
      return () => {
        clearInterval(timer);
      };
    }
  }, [
    setVisibleAnnouncement,
    announcements,
    isMouseOver,
    isReadMoreDialogOpen,
  ]);

  const handleContentOnReadMoreDialogOpen = useCallback(() => {
    setIsReadMoreDialogOpen(true);
    setIsMouseOver(false);
  }, [setIsReadMoreDialogOpen, setIsMouseOver]);

  const handleContentOnReadMoreDialogClose = useCallback(() => {
    setIsReadMoreDialogOpen(false);
  }, [setIsReadMoreDialogOpen]);

  const handleContentOnMouseEnter = useCallback(() => {
    setIsMouseOver(true);
  }, [setIsMouseOver]);

  const handleContentOnMouseLeave = useCallback(() => {
    setIsMouseOver(false);
  }, [setIsMouseOver]);

  return {
    announcements: announcements || [],
    visibleAnnouncement,
    loading: announcementsLoading,
    failedToLoad: failedToLoadAnnouncements,
    showAllAnnouncementsDialog,
    handleReadAllButtonClick,
    handleShowAllAnnouncementsDialogClose,
    handleContentOnMouseEnter,
    handleContentOnMouseLeave,
    handleContentOnReadMoreDialogOpen,
    handleContentOnReadMoreDialogClose,
  };
};
